import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Button, TextField,
    Stack, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, InputAdornment,
    Chip
} from "@mui/material";
import { Add, Edit, Delete, Search, LocationOn, CurrencyRupee } from "@mui/icons-material";
import { collection, getDocs, doc, setDoc, deleteDoc, query, where, orderBy } from "firebase/firestore";
import { db } from "../../firebase";

function Locationtype() {
    const [types, setTypes] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [open, setOpen] = useState(false);

    // Form States
    const [editId, setEditId] = useState(null);
    const [typeName, setTypeName] = useState("");
    const [baseFee, setBaseFee] = useState(""); // New Fee Field

    const schoolId = "zIlbJM5BjyrlEhg7fWKs";

    useEffect(() => { loadTypes(); }, []);

    const loadTypes = async () => {
        try {
            const q = query(
                collection(db, "location_types"),
                where("schoolId", "==", schoolId),
                orderBy("fee", "asc") // Firestore sorts this first
            );

            const snap = await getDocs(q);
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            // REMOVE the .sort by name here to keep the fee order
            setTypes(data);
        } catch (e) {
            console.error("Error loading types:", e);
            // If it fails with an "index required" error, check your console for a link
        }
    };

    const handleSave = async () => {
        if (!typeName.trim()) return;

        const docId = editId || doc(collection(db, "location_types")).id;
        try {
            await setDoc(doc(db, "location_types", docId), {
                name: typeName,
                fee: parseFloat(baseFee) || 0, // Store as Number
                schoolId: schoolId,
                updatedAt: new Date().toISOString()
            });
            setOpen(false);
            setTypeName("");
            setBaseFee("");
            loadTypes();
        } catch (e) {
            console.error("Error saving:", e);
        }
    };

    const handleEdit = (t) => {
        setEditId(t.id);
        setTypeName(t.name);
        setBaseFee(t.fee || "");
        setOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this location type?")) {
            await deleteDoc(doc(db, "location_types", id));
            loadTypes();
        }
    };

    const filteredTypes = types.filter(t =>
        t.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box p={2}>
            {/* Header Section */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: 'white' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOn color="primary" /> Location & Fee Setup
                    </Typography>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            placeholder="Search types..."
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                            }}
                        />
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => { setEditId(null); setTypeName(""); setBaseFee(""); setOpen(true); }}
                        >
                            Add New
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {/* Table Section */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell width={70} sx={{ fontWeight: 'bold' }}>Sr.</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Location Type</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Default Fee (₹)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredTypes.map((t, i) => (
                            <TableRow key={t.id} hover>
                                <TableCell>{i + 1}</TableCell>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={`₹${t.fee || 0}`}
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleEdit(t)} size="small">
                                        <Edit color="primary" fontSize="small" />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(t.id)} size="small">
                                        <Delete color="error" fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filteredTypes.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                    No records found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add/Edit Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editId ? "Update Location Type" : "Create New Location Type"}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Type Name (e.g., Boarding)"
                            placeholder="Enter location type..."
                            value={typeName}
                            onChange={e => setTypeName(e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Default Fee Amount"
                            type="number"
                            placeholder="0.00"
                            value={baseFee}
                            onChange={e => setBaseFee(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><CurrencyRupee fontSize="small" /></InputAdornment>,
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        {editId ? "Update Record" : "Save Record"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Locationtype;